/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes } from 'preact/compat';
import { Container, classes } from '@adobe-commerce/elsie/lib';
import { ProductModel } from '@/pdp/data/models';
import {
  getFetchedProductData,
  setProductConfigurationValues,
} from '@/pdp/api';
import { DownloadableOptions } from '@/pdp/components/DownloadableOptions';
import { useProductData, getAttributesById } from '@/pdp/lib/useProductData';

export interface ProductDownloadableOptionsProps
  extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
}

export const ProductDownloadableOptions: Container<
  ProductDownloadableOptionsProps,
  ProductModel | null
> = ({ initialData = null, scope, children, ...props }) => {
  const { data, values } = useProductData({
    scope,
    initialData,
    initialValues: { optionsUIDs: [] },
  });

  const handleLinkChange = (uid: string, selected: boolean) => {
    const prevUIDs = values.optionsUIDs || [];
    let newOptionsUIDs;

    if (!selected) {
      newOptionsUIDs = prevUIDs.filter((id) => id !== uid);
    } else if (prevUIDs.includes(uid)) {
      newOptionsUIDs = prevUIDs;
    } else {
      newOptionsUIDs = [...prevUIDs, uid];
    }

    setProductConfigurationValues((prev) => ({
      ...prev,
      optionsUIDs: newOptionsUIDs,
    }));
  };

  const downloadableAttributes = getAttributesById(data, 'ac_downloadable');

  if (!data) return null;

  if (downloadableAttributes.length === 0) return null;

  return (
    <div
      {...props}
      className={classes(['pdp-downloadable-options', props.className])}
    >
      {downloadableAttributes.map((downloadable, index) => (
        <DownloadableOptions
          key={`downloadable-${index}`}
          label={downloadable.label}
          value={downloadable.value}
          onLinkChange={handleLinkChange}
          currency={data.prices?.final?.currency}
          className={props.className}
        />
      ))}
    </div>
  );
};

ProductDownloadableOptions.getInitialData = getFetchedProductData;
